import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, Smartphone as PhoneIcon, QrCode, LogOut } from 'lucide-react';
import AddToCalendar from '@/components/AddToCalendar';
import ShareButton from '@/components/ShareButton';
import { api } from '@/api';

/**
 * What someone sees after registering.
 *
 * The QR ticket is deliberately NOT shown here. It lives in the app, which is
 * what we want them to install — showing the code on this page would remove the
 * reason to. The ticket code stays visible as a fallback, and the confirmation
 * email carries a scannable QR for anyone who never installs.
 */
export default function RegistrationSuccess({ ticket, event, onSignOut }) {
  const { t } = useTranslation();
  const [signingOut, setSigningOut] = useState(false);

  /*
   * Registering signs you in, and until now nothing signed you out.
   *
   * This page gets opened on shared machines — a reception desk, a laptop
   * passed around an office registering a team — where the next person landed
   * on someone else's session. Clearing it has to be possible from the screen
   * that created it.
   */
  const signOut = async () => {
    setSigningOut(true);
    try {
      await api.auth.logout();
      onSignOut?.();
    } finally {
      setSigningOut(false);
    }
  };
  const hasStoreLinks = Boolean(event?.store?.ios || event?.store?.android);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-brand/30 rounded-2xl p-6 sm:p-8"
    >
      {/* Confirmation */}
      <div className="text-center mb-7">
        <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-black" />
        </div>
        {/* A returning sign-in gets different copy: we did not just email them,
            and telling them we did sends them hunting for a message that is
            weeks old. */}
        <h3 className="font-heading font-black uppercase text-white text-3xl leading-tight">
          {t(ticket.returning ? 'success.returningTitle' : 'success.title')}
        </h3>
        <p className="text-white/50 text-sm mt-2 font-body">
          {t(ticket.returning ? 'success.returningSubtitle' : 'success.subtitle', { email: ticket.email })}
        </p>
      </div>

      {/* The ask */}
      <p className="label-tag text-brand text-[10px] mb-2.5">{t('success.nextStep')}</p>
      <div className="bg-brand rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <PhoneIcon className="w-6 h-6 text-black shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <h4 className="font-heading font-black uppercase text-black text-xl leading-tight">
              {t('success.downloadTitle')}
            </h4>
            <p className="text-black/70 text-sm font-body mt-1.5">{t('success.downloadBody')}</p>
          </div>
        </div>

        {/*
          The official badges, same as the App section further down the page —
          people recognise them far faster than a styled text button, and having
          the two places disagree makes the card look like a different product.
          Both always show so this reads as a real destination while the
          listings are in review; until the CMS carries the real URLs they point
          at '#' and the note stays up, rather than letting a dead button imply
          a download is waiting.

          Both files are 1084x327, so a shared height lines them up exactly.
        */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <a href={event?.store?.ios || '#'} aria-label={t('microsite.appStoreLabel')}>
            <img
              src="/buttons/app-store.png"
              alt={t('microsite.appStoreLabel')}
              className="h-11 w-auto hover:opacity-85 transition"
            />
          </a>
          <a href={event?.store?.android || '#'} aria-label={t('microsite.googlePlayLabel')}>
            <img
              src="/buttons/google-play.png"
              alt={t('microsite.googlePlayLabel')}
              className="h-11 w-auto hover:opacity-85 transition"
            />
          </a>
        </div>

        {!hasStoreLinks && (
          <p className="label-tag text-black/50 text-[10px] mt-3">{t('microsite.appComingSoon')}</p>
        )}
      </div>

      {/* Reminder */}
      <div className="mb-6">
        <AddToCalendar event={event} />
      </div>

      {/* Pass it on, while they are still on the high of having booked. */}
      <div className="mb-6">
        <ShareButton eventDate={event?.event_date} />
      </div>

      {/* Ticket code as the fallback for anyone who never installs the app. */}
      <div className="border-t border-white/10 pt-5 flex items-center gap-3">
        <QrCode className="w-5 h-5 text-white/25 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="label-tag text-white/30 text-[10px]">{t('success.codeLabel')}</p>
          <p className="font-heading font-bold text-brand-mid text-base tracking-[0.14em]">{ticket.ticket_code}</p>
        </div>
      </div>

      {/* Sign out, for the shared machine this was opened on. */}
      <div className="border-t border-white/10 mt-5 pt-4 flex items-center justify-between gap-3">
        <p className="text-white/30 text-xs font-body truncate">{ticket.email}</p>
        <button
          type="button"
          onClick={signOut}
          disabled={signingOut}
          className="inline-flex items-center gap-1.5 shrink-0 text-white/50 hover:text-white
                     font-heading font-bold uppercase text-[11px] px-3 py-1.5 rounded-lg
                     border border-white/15 disabled:opacity-50"
        >
          <LogOut className="w-3.5 h-3.5" /> {t('success.signOut')}
        </button>
      </div>
    </motion.div>
  );
}
